import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, Linkedin } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const TEAM = [
  {
    n: "Dott. Alessandro Botta",
    role: "Partner · Strategia & Sviluppo",
    bio:
      "Background in advisory strategico per PMI manifatturiere e di servizi. Si occupa di piani industriali, riorganizzazioni e percorsi di crescita strutturata. Ha lavorato fianco a fianco con imprenditori in operazioni di passaggio generazionale, M&A bottom-up e ingresso in nuovi mercati.",
    img: "https://images.unsplash.com/photo-1584940120505-117038d90b05?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
    linkedin: "#",
  },
  {
    n: "Dott. Francesco Varriale",
    role: "Partner · Finanza Agevolata",
    bio:
      "Esperto di finanza agevolata regionale, nazionale ed europea. Costruisce business plan finanziabili e accompagna l'azienda end-to-end, dall'individuazione del bando alla rendicontazione. In carriera ha gestito pratiche per oltre 18 milioni di euro di agevolazioni concesse.",
    img: "https://images.pexels.com/photos/32844862/pexels-photo-32844862.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720",
    linkedin: "#",
  },
];

const VALUES = [
  { k: "Concretezza", v: "Niente buzzword. Ogni raccomandazione deve essere implementabile entro la settimana." },
  { k: "Selezione", v: "Lavoriamo con un numero limitato di clienti all'anno. È l'unico modo per garantire la qualità che promettiamo." },
  { k: "Affiancamento", v: "I partner sono sul progetto. Niente delega a junior, niente gestione da remoto." },
  { k: "Misurabilità", v: "Definiamo a inizio mandato i numeri che cambiano. E li difendiamo trimestre dopo trimestre." },
];

export default function About() {
  useReveal();
  return (
    <div data-testid="page-about">
      <section className="bv-container pt-20 md:pt-28 pb-20">
        <p className="overline reveal">/ Chi siamo</p>
        <h1 className="h-display text-5xl md:text-7xl mt-6 max-w-[18ch] reveal">
          Una boutique di consulenza nata per imprenditori che pretendono risultati.
        </h1>
        <p className="mt-10 max-w-3xl text-[18px] text-[#0E2E4D]/75 leading-relaxed reveal">
          BV Business Consulting è una società di consulenza strategica indipendente. Affianchiamo
          PMI italiane lungo le tre leve che, dalla nostra esperienza, fanno la differenza tra
          una crescita effimera e una crescita strutturale: <em>strategia</em>, <em>operatività</em>, <em>finanza</em>.
        </p>
      </section>

      {/* Manifesto */}
      <section className="bg-[#0E2E4D] text-[#F5F2EB]">
        <div className="bv-container py-24 md:py-32">
          <p className="overline reveal" style={{ color: "#36BFC4" }}>/ Manifesto</p>
          <div className="mt-10 grid grid-cols-12 gap-8">
            <h2 className="col-span-12 md:col-span-7 h-display text-4xl md:text-5xl leading-[1.1] reveal">
              "Non vendiamo presentazioni. Costruiamo decisioni che si misurano in EBITDA, in liquidità,
              in commesse vinte. Tutto il resto è folklore."
            </h2>
            <div className="col-span-12 md:col-span-4 md:col-start-9 reveal">
              <p className="text-[15px] text-[#F5F2EB]/75 leading-relaxed">
                BV nasce dall'incontro di due professionalità complementari: una visione strategica orientata
                all'industria e un'expertise operativa nella finanza agevolata. Insieme, danno all'imprenditore
                un interlocutore unico per ogni decisione di crescita.
              </p>
              <p className="mt-6 font-mono text-[11px] tracking-[0.22em] uppercase text-[#36BFC4]">
                — A. Botta &amp; F. Varriale, fondatori
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="bv-container py-24 md:py-32" data-testid="about-team">
        <p className="overline reveal">/ Partner</p>
        <h2 className="h-display text-5xl md:text-6xl mt-6 max-w-[14ch] reveal">
          I partner che firmano ogni progetto.
        </h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0E2E4D]/10">
          {TEAM.map((p, i) => (
            <article
              key={p.n}
              data-testid={`about-partner-${i}`}
              className="bg-[#F5F2EB] p-8 md:p-12 reveal"
              style={{ "--reveal-delay": `${i * 120}ms` }}
            >
              <div className="aspect-[4/5] bg-[#0E2E4D] overflow-hidden">
                <img src={p.img} alt={p.n} className="w-full h-full object-cover grayscale-[15%]" />
              </div>
              <div className="mt-8">
                <h3 className="h-display text-3xl md:text-4xl">{p.n}</h3>
                <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#2BA8AD] mt-3">{p.role}</p>
                <p className="mt-6 text-[15px] text-[#0E2E4D]/80 leading-relaxed">{p.bio}</p>
                <div className="mt-8 flex items-center gap-6">
                  <a href={p.linkedin} className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-mono link-underline" data-testid={`partner-linkedin-${i}`}>
                    <Linkedin size={14} /> LinkedIn
                  </a>
                  <Link to="/contatti" className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase font-mono link-underline" data-testid={`partner-contact-${i}`}>
                    <Mail size={14} /> Contatta
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#ECE7DC]">
        <div className="bv-container py-24 md:py-32">
          <p className="overline reveal">/ Valori</p>
          <h2 className="h-display text-5xl md:text-6xl mt-6 max-w-[18ch] reveal">
            Quattro principi su cui non transigiamo.
          </h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#0E2E4D]/10">
            {VALUES.map((v, i) => (
              <div key={v.k} className="bg-[#ECE7DC] p-10 reveal" style={{ "--reveal-delay": `${i * 90}ms` }} data-testid={`value-${i}`}>
                <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#2BA8AD]">{`/ 0${i + 1}`}</p>
                <h3 className="h-display text-3xl md:text-4xl mt-3">{v.k}</h3>
                <p className="mt-4 text-[15px] text-[#0E2E4D]/75 leading-relaxed">{v.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bv-container py-24 md:py-32">
        <div className="grid grid-cols-12 gap-8 items-end">
          <h2 className="col-span-12 md:col-span-8 h-display text-4xl md:text-6xl leading-[1] max-w-[20ch] reveal">
            Pronto a parlare di numeri?
          </h2>
          <div className="col-span-12 md:col-span-4 flex md:justify-end reveal">
            <Link
              to="/contatti"
              data-testid="about-cta-contact"
              className="inline-flex items-center gap-3 bg-[#0E2E4D] text-[#F5F2EB] px-8 py-5 text-[12px] tracking-[0.18em] uppercase font-mono hover:bg-[#2BA8AD] transition-colors"
            >
              Richiedi una consulenza <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
